import { useEffect, useRef, useState } from 'react';
import { Color, fill, picturePathOf, pictureThumbPathOf, Size, t, type VisitNotePhoto } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { linkedAssetLine } from './VisitPhotoAsset';
import styles from './VisitPhotos.module.css';

/**
 * 🔒 Decision 4320 (the owner's A5a): whether the photo control opens the camera first. A coarse pointer is the browser's word
 * that the device is held, and the file input's `capture` asks it for the camera; the browser decides whether one opens, and a
 * device with no camera app gets the chooser. A browser with no media queries reads as a desk.
 */
export function cameraFirst(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

/**
 * 🔒 The photo control (decision 4320): one shared control on each line's card and in the Add Note window. Camera first, it reads
 * Take Photo, which opens the camera, with Choose a File beside it; elsewhere it reads Add Photo and opens the file chooser. The
 * pictures chosen are handed to `onPick`; nothing is sent until the window that holds them saves.
 */
export function VisitPhotoControl({ onPick, disabled = false, attribute }: { onPick: (files: File[]) => void; disabled?: boolean; attribute?: string }) {
  const [camera] = useState(cameraFirst);
  const cameraInput = useRef<HTMLInputElement | null>(null);
  const chooserInput = useRef<HTMLInputElement | null>(null);
  const picked = (input: HTMLInputElement) => {
    const files = [...(input.files ?? [])];
    // The same picture chosen twice in a row still answers, since the input is emptied once read.
    input.value = '';
    if (files.length > 0) onPick(files);
  };
  return (
    <span className={styles.control} data-visit-photo-control={camera ? 'camera' : 'chooser'} {...(attribute ? { [attribute]: 'true' } : {})}>
      {camera ? <input ref={cameraInput} className={styles.file} type="file" accept="image/*" capture="environment" name="visit-photo-camera" aria-hidden="true" tabIndex={-1} onChange={(event) => picked(event.target)} /> : null}
      <input ref={chooserInput} className={styles.file} type="file" accept="image/*" multiple name="visit-photo-file" aria-hidden="true" tabIndex={-1} onChange={(event) => picked(event.target)} />
      {camera ? (
        <>
          <span data-visit-photo-take="true"><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.photo.take')]} disabled={disabled} actions={[() => cameraInput.current?.click()]} /></span>
          <span data-visit-photo-choose="true"><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.photo.choose')]} disabled={disabled} actions={[() => chooserInput.current?.click()]} /></span>
        </>
      ) : <span data-visit-photo-add="true"><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.bar.addPhoto')]} disabled={disabled} actions={[() => chooserInput.current?.click()]} /></span>}
    </span>
  );
}

/** A picture chosen in a window and not yet sent, drawn from the browser's own copy; jsdom and an old browser draw its name alone. */
export function PicturePreview({ file }: { file: File }) {
  const image = useRef<HTMLImageElement | null>(null);
  const drawable = typeof URL.createObjectURL === 'function';
  // The browser's copy is made when the picture is drawn and let go when it leaves the window, so a removed picture holds nothing.
  useEffect(() => {
    if (!drawable || !image.current) return undefined;
    const made = URL.createObjectURL(file);
    image.current.src = made;
    return () => URL.revokeObjectURL(made);
  }, [file, drawable]);
  return drawable ? <img ref={image} className={styles.preview} alt={file.name} /> : <span className={styles.previewName}>{file.name}</span>;
}

/** What the full-size window shows: the picture, its caption, and who took it when. */
export interface OpenPhoto {
  photo: VisitNotePhoto;
  /** Who took it, the note's author, and when, in the viewer's zone. */
  name: string;
  at: string;
}

const HELD_KEYS = { Scanning: 'serviceReport.photo.scanning', Blocked: 'serviceReport.photo.blocked', Gone: 'serviceReport.photo.gone' } as const;

/**
 * 🔒 A note's pictures as square thumbnails, the caption under each (decision 4320; the owner's A2a): three to a row under the
 * phone breakpoint and more above it. A tap opens the picture full size; a picture still held by its scan draws a placeholder
 * naming its state and opens nothing (decision 0234). Each reads the path the visit's answer names for it, the visit's
 * picture route for a worker its assignment names (decision 13210).
 */
export function VisitPhotoGrid({ photos, name, at, apiBaseUrl, onOpen }: { photos: readonly VisitNotePhoto[]; name: string; at: string; apiBaseUrl: string; onOpen: (open: OpenPhoto) => void }) {
  if (photos.length === 0) return null;
  return (
    <ul className={styles.thumbs} data-visit-thumbs={photos.length}>
      {photos.map((photo) => (
        <li key={photo.fileId} className={styles.thumbCell} data-report-photo-row={photo.fileId}>
          {photo.held ? (
            <span className={styles.held} data-visit-thumb-held={photo.held}>{t(HELD_KEYS[photo.held])}</span>
          ) : (
            <button type="button" className={styles.thumb} aria-label={fill(t('serviceReport.photo.openLabel'), { name: photo.caption ?? photo.filename })} onClick={() => onOpen({ photo, name, at })} data-visit-thumb={photo.fileId}>
              <img className={styles.thumbImage} src={`${apiBaseUrl}${pictureThumbPathOf(photo.fileId, photo.path)}`} alt={photo.caption ?? photo.filename} loading="lazy" decoding="async" data-report-photo={photo.fileId} />
            </button>
          )}
          {photo.caption ? <span className={styles.caption} data-visit-thumb-caption={photo.fileId}>{photo.caption}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * 🔒 The picture full size in the shared modal (piece 5; decision 4320): its caption, who took it and when, and Open for the original,
 * each on the path the visit's answer names for the picture (decision 13210), which `onOriginal` is handed. 🔒 Decision 13310: the
 * asset the photo stands on, and Link to Asset where `onLinkAsset` is handed, which a viewer the note's writer rule names is.
 */
export function VisitPhotoViewer({ open, apiBaseUrl, onClose, onOriginal, onLinkAsset }: { open: OpenPhoto; apiBaseUrl: string; onClose: () => void; onOriginal: (path: string) => void; onLinkAsset?: (() => void) | null }) {
  const path = picturePathOf(open.photo.fileId, open.photo.path);
  return (
    <Modal open title={t('serviceReport.photo.view')} subtitle={open.photo.filename} onCancel={onClose} actions={[{ kind: 'close', label: t('modal.close'), onAct: onClose }]} width="wide" dataModal="visit-photo" backdropCloses>
      <div className={styles.viewer} data-visit-photo-view={open.photo.fileId}>
        <img className={styles.viewerImage} src={`${apiBaseUrl}${path}`} alt={open.photo.caption ?? open.photo.filename} />
        {open.photo.caption ? <p className={styles.viewerCaption}>{open.photo.caption}</p> : null}
        <p className={styles.viewerBy}>{fill(t('serviceReport.photo.takenBy'), { name: open.name, at: open.at })}</p>
        {open.photo.asset ? <p className={styles.viewerBy} data-visit-photo-asset={open.photo.asset.id}>{linkedAssetLine(open.photo.asset)}</p> : null}
        <span className={styles.viewerActs}>
          <span data-visit-photo-original={open.photo.fileId}><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.documents.open')]} actions={[() => onOriginal(path)]} /></span>
          {onLinkAsset ? <span data-visit-photo-link-asset={open.photo.fileId}><Button size={Size.Small} color={Color.Inactive} text={[t('serviceReport.photo.linkAsset')]} actions={[onLinkAsset]} /></span> : null}
        </span>
      </div>
    </Modal>
  );
}
